import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, HomeIcon, UsersIcon, GlobeIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Residency Services
{
  id: 'rs1',
  title: 'Proof of Residency Certificate',
  description: 'Apply for official documentation proving your residency status',
  category: 'Residency Services',
  estimatedTime: '5-7 business days',
  fee: '$35.00'
}, {
  id: 'rs2',
  title: 'Address Verification',
  description: 'Verify your current residential address for official purposes',
  category: 'Residency Services',
  estimatedTime: '2-3 business days',
  fee: '$25.00'
},
// Integration Programs
{
  id: 'ip1',
  title: 'Local Integration Program Information',
  description: 'Access information about newcomer integration programs and services',
  category: 'Integration Programs',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'ip2',
  title: 'Language Program Registration',
  description: 'Register for local language learning programs',
  category: 'Integration Programs',
  estimatedTime: '3-5 business days',
  fee: 'Varies by program'
}, {
  id: 'ip3',
  title: 'Community Orientation Session',
  description: 'Sign up for newcomer orientation and community information sessions',
  category: 'Integration Programs',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Residency Services':
      return <HomeIcon className="h-6 w-6 text-blue-600" data-id="element-3805" />;
    case 'Integration Programs':
      return <GlobeIcon className="h-6 w-6 text-green-600" data-id="element-3806" />;
    default:
      return <div className="h-6 w-6 text-gray-600" data-id="element-3807" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3808">
      <div className="p-5 flex flex-col h-full" data-id="element-3809">
        <div className="flex-1" data-id="element-3810">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3811">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3812">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3813">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3814">
            <div className="flex items-center" data-id="element-3815">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3816">
                ⏱
              </span>
              <span data-id="element-3817">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3818">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3819">
                💰
              </span>
              <span data-id="element-3820">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3821">
            Apply Now
            <span className="ml-1" data-id="element-3822">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3823">
      <div className="flex items-center mb-4" data-id="element-3824">
        <CategoryIcon category={category} data-id="element-3825" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3826">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3827">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3828" />)}
      </div>
    </div>;
};
export const ImmigrationResidencyPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3829">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3830">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3831">
          <a href="/" className="hover:text-gray-700" data-id="element-3832">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3833" />
          <span className="text-gray-900" data-id="element-3834">
            Immigration & Residency Services
          </span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3835">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3836">
            Immigration & Residency Services
          </h1>
          <p className="text-gray-600" data-id="element-3837">
            Access residency services and newcomer integration programs
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3838">
          <div className="max-w-md relative" data-id="element-3839">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3840">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3841" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3842" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3843" />)}
      </div>
    </div>;
};